import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { dashboardService } from '../services/dashboardService'
import type { DashboardListItem, DashboardRoleGroup } from '../types/dashboard'
import type { UserRole } from '../types/user'
import { useTenantProductType } from '../hooks/useTenantProductType'
import './Dashboard.css'

const roleGroupForRole = (role?: UserRole): DashboardRoleGroup => {
  if (!role) return 'admin'

  if (role === 'doctor' || role === 'physician-assistant' || role === 'therapist') {
    return 'clinician'
  }

  if (role === 'nurse') return 'nurse'
  if (role === 'receptionist') return 'reception'
  if (role === 'office-manager') return 'manager'

  if (role === 'system-admin' || role === 'it-support') {
    return 'system'
  }

  return 'admin'
}

const roleOptions: Array<{ value: DashboardRoleGroup; label: string }> = [
  { value: 'clinician', label: 'Clinician' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'reception', label: 'Reception' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Practice manager' },
  { value: 'system', label: 'System' },
]

const ListItemRow = ({ item }: { item: DashboardListItem }) => {
  const content = (
    <>
      <div className="list-item-title">{item.label}</div>
      {item.meta && <div className="list-item-meta">{item.meta}</div>}
      {item.badge && <span className="list-item-badge">{item.badge}</span>}
    </>
  )

  if (item.to) {
    return (
      <Link to={item.to} className="list-item" aria-label={item.label}>
        {content}
      </Link>
    )
  }

  return (
    <div className="list-item" aria-label={item.label}>
      {content}
    </div>
  )
}

export function Dashboard() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { isPptOnly } = useTenantProductType()
  const [rolePreview, setRolePreview] = useState<DashboardRoleGroup | 'auto'>('auto')
  const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost'

  const roleGroup = useMemo(() => {
    if (rolePreview !== 'auto') {
      return rolePreview
    }
    return roleGroupForRole(user?.role)
  }, [rolePreview, user?.role])

  const displayName = user ? `${user.firstName} ${user.lastName}` : 'team'
  const roleLabel = roleOptions.find((option) => option.value === roleGroup)?.label || 'Admin'

  const {
    data: summary,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboard-summary', roleGroup],
    queryFn: () => dashboardService.getSummary(roleGroup),
    enabled: !isPptOnly,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getStats(),
    enabled: !isPptOnly,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const {
    data: pptQueueSnapshot,
    isLoading: isPptQueueLoading,
    error: pptQueueError,
  } = useQuery({
    queryKey: ['dashboard-ppt-queue-snapshot'],
    queryFn: () => dashboardService.getPptQueueSnapshot(),
    enabled: isPptOnly,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const {
    data: pptPipeline,
    isLoading: isPptPipelineLoading,
    error: pptPipelineError,
  } = useQuery({
    queryKey: ['dashboard-ppt-pipeline'],
    queryFn: () => dashboardService.getPptPipeline(),
    enabled: isPptOnly,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const {
    data: pptBatchReadiness,
    isLoading: isPptBatchLoading,
    error: pptBatchError,
  } = useQuery({
    queryKey: ['dashboard-ppt-batch-readiness'],
    queryFn: () => dashboardService.getPptBatchReadiness(),
    enabled: isPptOnly,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const {
    data: pptExceptions,
    isLoading: isPptExceptionsLoading,
    error: pptExceptionsError,
  } = useQuery({
    queryKey: ['dashboard-ppt-exceptions'],
    queryFn: () => dashboardService.getPptExceptions(25),
    enabled: isPptOnly,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  if (!isPptOnly && isLoading) {
    return (
      <div className="dashboard" role="main" aria-live="polite">
        <h1>{t('navigation.dashboard')}</h1>
        <div className="loading" aria-label={t('accessibility.loading')}>
          {t('common.loading')}
        </div>
      </div>
    )
  }

  if (!isPptOnly && error) {
    return (
      <div className="dashboard" role="main">
        <h1>{t('navigation.dashboard')}</h1>
        <div className="error" role="alert" aria-live="assertive">
          {t('common.error')}: {t('dashboard.errorLoading') || 'Failed to load dashboard data'}
        </div>
      </div>
    )
  }

  if (isPptOnly) {
    const pptLoading = isPptQueueLoading || isPptPipelineLoading || isPptBatchLoading || isPptExceptionsLoading
    const pptError = pptQueueError || pptPipelineError || pptBatchError || pptExceptionsError

    if (pptLoading) {
      return (
        <div className="dashboard" role="main" aria-live="polite">
          <h1>{t('navigation.dashboard')}</h1>
          <div className="loading" aria-label={t('accessibility.loading')}>
            {t('common.loading')}
          </div>
        </div>
      )
    }

    if (pptError) {
      return (
        <div className="dashboard" role="main">
          <h1>{t('navigation.dashboard')}</h1>
          <div className="error" role="alert" aria-live="assertive">
            {t('common.error')}: {t('dashboard.errorLoading') || 'Failed to load dashboard data'}
          </div>
        </div>
      )
    }

    const exceptions = pptExceptions?.items || []

    return (
      <div className="dashboard" role="main">
        <div className="dashboard-header">
          <div>
            <h1>{t('navigation.dashboard')}</h1>
            <p className="dashboard-subtitle">PPT operations overview for claims workflow.</p>
            <div className="role-pill" aria-label="Product context: PPT">
              PPT focus
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-main">
            <section className="dashboard-panel" aria-label="Work queue snapshot">
              <div className="panel-header">
                <div>
                  <h2>Work queue snapshot</h2>
                  <p>Live counts for STC/MICS processing.</p>
                </div>
              </div>
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-value">{pptQueueSnapshot?.signed ?? 0}</div>
                  <div className="kpi-label">Signed</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptQueueSnapshot?.queued ?? 0}</div>
                  <div className="kpi-label">Queued</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptQueueSnapshot?.toPay ?? 0}</div>
                  <div className="kpi-label">To Pay</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptQueueSnapshot?.paid ?? 0}</div>
                  <div className="kpi-label">Paid</div>
                </div>
              </div>
            </section>

            <section className="dashboard-panel" aria-label="Pipeline status">
              <div className="panel-header">
                <div>
                  <h2>Pipeline status</h2>
                  <p>Status funnel for claims lifecycle.</p>
                </div>
              </div>
              <div className="kpi-grid">
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.signed ?? 0}</div><div className="kpi-label">Signed</div></div>
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.queued ?? 0}</div><div className="kpi-label">Queued</div></div>
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.toPay ?? 0}</div><div className="kpi-label">To Pay</div></div>
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.paid ?? 0}</div><div className="kpi-label">Paid</div></div>
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.rejected ?? 0}</div><div className="kpi-label">Rejected</div></div>
                <div className="kpi-card"><div className="kpi-value">{pptPipeline?.failed ?? 0}</div><div className="kpi-label">Failed</div></div>
              </div>
            </section>

            <section className="dashboard-panel" aria-label="Batch readiness">
              <div className="panel-header">
                <div>
                  <h2>Batch readiness</h2>
                  <p>Operational readiness for next submission cycle.</p>
                </div>
              </div>
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-value">{pptBatchReadiness?.signedCount ?? 0}</div>
                  <div className="kpi-label">Signed claims</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptBatchReadiness?.queuedCount ?? 0}</div>
                  <div className="kpi-label">Queued claims</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptBatchReadiness?.estimatedBatches ?? 0}</div>
                  <div className="kpi-label">Estimated batches</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{pptPipeline?.signedToToPayRate ?? 0}%</div>
                  <div className="kpi-label">Signed to To Pay</div>
                </div>
              </div>
            </section>
          </div>

          <aside className="dashboard-rail" aria-label="PPT actions and exceptions">
            <section className="rail-card" aria-label="Quick actions">
              <div className="panel-header">
                <h2>Quick actions</h2>
                <p>Common PPT workflows.</p>
              </div>
              <div className="list">
                <Link to="/stc-signing" className="list-item" aria-label="New STC or MICS form">
                  <div className="list-item-title">New STC/MICS form</div>
                  <div className="list-item-meta">Start manual entry and signature</div>
                </Link>
                <Link to="/reports/stc-status" className="list-item" aria-label="Open STC report">
                  <div className="list-item-title">Open STC report</div>
                  <div className="list-item-meta">Queue and submit signed claims</div>
                </Link>
              </div>
            </section>

            <section className="rail-card" aria-label="Exception queue">
              <div className="panel-header">
                <h2>Exception queue</h2>
                <p>{pptExceptions?.totalFailed ?? 0} failed claims requiring attention.</p>
              </div>
              <div className="list ppt-exception-list">
                {exceptions.map((item) => (
                  <ListItemRow key={item.id || item.label} item={item} />
                ))}
                {exceptions.length === 0 && <div className="empty-state">No failed claims right now.</div>}
              </div>
            </section>
          </aside>
        </div>
      </div>
    )
  }

  const quickActions = summary?.quickActions || []
  const highlights = summary?.highlights || []
  const queue = summary?.queue || []
  const kpis = summary?.kpis || []
  const insights = summary?.insights || []
  const alerts = summary?.alerts || []
  const shortcuts = summary?.shortcuts || []

  return (
    <div className="dashboard" role="main">
      <div className="dashboard-header">
        <div>
          <h1>{t('navigation.dashboard')}</h1>
          <p className="dashboard-subtitle">
            {t('dashboard.welcome') || `Welcome back, ${displayName}.`}
          </p>
          <div className="role-pill" aria-label={`Role focus: ${summary?.roleLabel || roleLabel}`}>
            {summary?.roleLabel || roleLabel} focus
          </div>
        </div>
        {isDev && (
          <div className="role-selector" aria-label="Role preview">
            <label htmlFor="role-preview">Preview role</label>
            <select
              id="role-preview"
              value={rolePreview}
              onChange={(event) => setRolePreview(event.target.value as DashboardRoleGroup | 'auto')}
            >
              <option value="auto">Auto</option>
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-main">
          {stats && (
            <section className="dashboard-panel" aria-label="Practice snapshot">
              <div className="panel-header">
                <div>
                  <h2>Practice snapshot</h2>
                  <p>Key counts for the clinic right now.</p>
                </div>
              </div>
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-value">{stats.totalPatients}</div>
                  <div className="kpi-label">Total patients</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{stats.activePatients}</div>
                  <div className="kpi-label">Active patients</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{stats.todayAppointments}</div>
                  <div className="kpi-label">Appointments today</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{stats.upcomingAppointments}</div>
                  <div className="kpi-label">Upcoming appointments</div>
                </div>
              </div>
            </section>
          )}
          <section className="dashboard-panel" aria-label="Quick actions">
            <div className="panel-header">
              <div>
                <h2>Quick actions</h2>
                <p>Start the most common tasks without searching.</p>
              </div>
            </div>
            <div className="action-grid">
              {quickActions.map((action) => (
                <Link
                  key={action.id || `${action.label}-${action.to}`}
                  to={action.to}
                  className={`action-card${action.tone === 'primary' ? ' action-card-primary' : ''}`}
                  aria-label={action.label}
                >
                  <div className="action-title">{action.label}</div>
                  <div className="action-description">{action.description}</div>
                </Link>
              ))}
              {quickActions.length === 0 && <div className="empty-state">No quick actions available.</div>}
            </div>
          </section>

          <section className="dashboard-panel" aria-label="My day">
            <div className="panel-header">
              <div>
                <h2>My day</h2>
                <p>Everything you need to act on today.</p>
              </div>
            </div>
            <div className="panel-columns">
              <div className="panel-card">
                <h3>Today highlights</h3>
                <div className="list">
                  {highlights.map((item) => (
                    <ListItemRow key={item.id || item.label} item={item} />
                  ))}
                  {highlights.length === 0 && <div className="empty-state">No highlights to show.</div>}
                </div>
              </div>
              <div className="panel-card">
                <h3>Next up</h3>
                <div className="list">
                  {queue.map((item) => (
                    <ListItemRow key={item.id || item.label} item={item} />
                  ))}
                  {queue.length === 0 && <div className="empty-state">No upcoming items.</div>}
                </div>
              </div>
            </div>
          </section>

          <section className="dashboard-panel" aria-label="Role insights">
            <div className="panel-header">
              <div>
                <h2>{summary?.roleLabel || roleLabel} insights</h2>
                <p>{summary?.roleDescription || 'Focus items tailored to your role.'}</p>
              </div>
            </div>
            <div className="kpi-grid">
              {kpis.map((kpi) => (
                <div key={kpi.label} className="kpi-card">
                  <div className="kpi-value">{kpi.value}</div>
                  <div className="kpi-label">{kpi.label}</div>
                  {kpi.trend && <div className="kpi-trend">{kpi.trend}</div>}
                </div>
              ))}
              {kpis.length === 0 && <div className="empty-state">No role metrics yet.</div>}
            </div>
          </section>

          <section className="dashboard-panel" aria-label="Insights to action">
            <div className="panel-header">
              <div>
                <h2>Insights to action</h2>
                <p>Focused items that move today forward.</p>
              </div>
            </div>
            <div className="panel-card">
              <div className="list">
                {insights.map((item) => (
                  <ListItemRow key={item.id || item.label} item={item} />
                ))}
                {insights.length === 0 && <div className="empty-state">No insights to show.</div>}
              </div>
            </div>
          </section>
        </div>

        <aside className="dashboard-rail" aria-label="Supporting info">
          <section className="rail-card" aria-label="Alerts">
            <div className="panel-header">
              <h2>Alerts</h2>
              <p>Time-sensitive items.</p>
            </div>
            <div className="list">
              {alerts.map((item) => (
                <ListItemRow key={item.id || item.label} item={item} />
              ))}
              {alerts.length === 0 && <div className="empty-state">No alerts right now.</div>}
            </div>
          </section>

          <section className="rail-card" aria-label="Shortcuts">
            <div className="panel-header">
              <h2>Shortcuts</h2>
              <p>Jump straight to key areas.</p>
            </div>
            <div className="list">
              {shortcuts.map((item) => (
                <ListItemRow key={item.id || item.label} item={item} />
              ))}
              {shortcuts.length === 0 && <div className="empty-state">No shortcuts configured.</div>}
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

